/**
 * Workflow engine — core types.
 *
 * Client-safe by construction: nothing in this module (or anything it imports)
 * may reach `@repo/database`, `server-only`, or the state loader. The catalog,
 * the evaluator and every UI surface read these types from
 * `@repo/core/client`; only `state-loader.ts` touches the database.
 *
 * A workflow is a declarative playbook: an ordered list of steps, each of which
 * names an existing dashboard page and a *predicate over observed property
 * state*. Completion is never declared by the product — it is observed, exactly
 * the way `onboarding-steps.ts` derives its suggestions. A run only ever adds
 * two overrides on top of that: "skip this step" and "I did this one off-app".
 */

import type { SubjectType } from "../types/property";
import type { SubjectModeConfig } from "../utils/subject-mode";
// Type-only, and deliberately one-directional at runtime: the descriptors live
// beside the estimator that reads them (`spend.ts`), which imports this module
// for `WorkflowDefinition`. `import type` is erased, so the pair never forms a
// runtime cycle — and `spend.ts` is the single exporter, so the two barrels do
// not both claim the name.
import type { WorkflowSpendDescriptor } from "./spend";
import type {
	OrgCountKey,
	OrgFlagKey,
	PropertyCountKey,
	PropertyFlagKey,
	WorkflowOrganizationState,
	WorkflowState,
} from "./state";

// ── Audiences ──────────────────────────────────────────────────────────────────

export type WorkflowAudienceKey = "agencies" | "pr" | "inhouse" | "content";

export const workflowAudiences: Array<{
	key: WorkflowAudienceKey;
	label: string;
}> = [
	{ key: "agencies", label: "Agencies" },
	{ key: "pr", label: "PR & Comms" },
	{ key: "inhouse", label: "In-house" },
	{ key: "content", label: "Content" },
];

// ── Step signals ───────────────────────────────────────────────────────────────

/**
 * The small piece of evidence a step panel renders beside its criterion.
 * Moved verbatim from the design-lab fixtures so the prototyped primitives
 * (`StepSignal`, `Sparkline`, `BeforeAfter`) render live data unchanged.
 */
export type WorkflowStepSignal =
	| {
			kind: "count";
			label: string;
			value: number;
			target?: number;
			caption?: string;
	  }
	| { kind: "score"; label: string; value: number; target: number }
	| {
			kind: "trend";
			label: string;
			series: number[];
			change: number;
			suffix?: string;
			lowerIsBetter?: boolean;
	  }
	| { kind: "checklist"; items: Array<{ label: string; done: boolean }> }
	| {
			kind: "beforeAfter";
			label: string;
			before: string;
			after: string;
			tone?: "positive" | "negative" | "neutral";
			caption?: string;
			/** Raw values over time; the primitive draws them so up = better. */
			series?: number[];
			/** True for position-style metrics (ranks) where lower is better. */
			lowerIsBetter?: boolean;
	  };

// ── Events ─────────────────────────────────────────────────────────────────────

/**
 * Things that happen to a property and carry a timestamp. `since` predicates
 * anchor to `run.startedAt`, so a repeatable, tool-shaped workflow (W23) is not
 * already complete the moment someone opens it for the second time.
 */
export type WorkflowEventKey =
	| "reportCompleted"
	| "reportExported"
	| "promptAdded"
	| "competitorAdded"
	| "keyMessageAdded"
	/**
	 * Key messages split by sentiment. A rebrand or a campaign is about the
	 * *new* positioning, and a crisis is about the claim plus the answer to it,
	 * so those steps have to anchor on the sentiment they are asking for rather
	 * than on "a key message, any key message, exists".
	 */
	| "positiveKeyMessageAdded"
	| "negativeKeyMessageAdded"
	| "projectCreated"
	| "projectCompleted"
	| "projectExported"
	| "annotationCreated"
	| "siteAuditCompleted"
	| "pipelineRunCreated"
	| "pipelineRunCompleted"
	| "pipelineRevisionCreated"
	| "placementScored"
	| "coverageGroupCreated"
	| "coverageGroupExported"
	| "locationAdded"
	| "faqGenerated"
	| "snapshotEdited"
	| "pageImported";

// ── Feature gates and presentation hints ───────────────────────────────────────

/**
 * Steps whose observable does not exist yet. The catalog can name them today;
 * `WORKFLOW_FEATURES` hides them until the PR that builds the signal flips the
 * flag, so a step never sits permanently unachievable in front of a user.
 */
export type WorkflowFeatureKey = "negativeMessageAlerts" | "messageMatches";

/** Presentation-only: which existing hook the step panel should hydrate with. */
export type WorkflowEnrichmentKey =
	| "coverageScore"
	| "sovTrend"
	| "messageTrend"
	| "rankTrend"
	| "seoGaps";

// ── Predicates ─────────────────────────────────────────────────────────────────

export type WorkflowPredicate =
	/** A counted thing on the property reached a threshold. Dotted keys allowed. */
	| { kind: "count"; key: PropertyCountKey; gte: number }
	/** A boolean fact about the property. */
	| { kind: "flag"; key: PropertyFlagKey }
	| { kind: "orgCount"; key: OrgCountKey; gte: number }
	| { kind: "orgFlag"; key: OrgFlagKey }
	/** `run.context.tag ?? tag` appears among the property's prompt tags. */
	| { kind: "promptTag"; tag: string }
	| { kind: "analyticsPlatform"; platform: string }
	/**
	 * The event happened at or after `run.startedAt`. False when there is no
	 * run at all. With `scope: "target"` it reads the bound target's event
	 * slice instead of the property-wide one — false until the run is bound, so
	 * one colleague's pipeline run never checks off another's step.
	 */
	| { kind: "since"; event: WorkflowEventKey; scope?: "target" }
	/** The run has a property bound (and, optionally, of a matching subject). */
	| { kind: "bound"; subjectType?: SubjectType }
	/**
	 * The run says what it is about: `run.context.targetKey` is set. False
	 * without a run and false while a per-target run is still unbound, which is
	 * what lets a step read "the market this run covers is chosen" without
	 * caring whether the market was created now or already existed.
	 */
	| { kind: "target" }
	/**
	 * A LOCATION child of this property. `some` asks whether *any* child
	 * qualifies; `bound` asks about the one this run is about
	 * (`run.context.locationPropertyId`) and is false while the run is still
	 * unbound — like a target-scoped `since`, nothing completes until the run
	 * says which market it is about.
	 */
	| {
			kind: "locations";
			some?: "hasOwnCompetitors";
			bound?: "hasOwnCompetitors";
	  }
	| { kind: "all"; of: WorkflowPredicate[] }
	| { kind: "any"; of: WorkflowPredicate[] }
	/** No observable exists: only an explicit "mark done" completes the step. */
	| { kind: "manual" };

// ── Steps and definitions ──────────────────────────────────────────────────────

export interface WorkflowStepSignalContext {
	state: WorkflowState;
	run: WorkflowRunLike | null;
}

export interface WorkflowStep {
	id: string;
	title: string;
	/** Human label for the dashboard this step happens on. */
	page: string;
	/**
	 * Property-relative route ("/discovery-queries"), or "org:/…" for a page
	 * that lives on the organization rather than the property. Must be a member
	 * of `WORKFLOW_ROUTES` — `catalog.test.ts` enforces it.
	 */
	route: string;
	/** One line: what the runner checks off from property state. */
	criterion: string;
	why: string;
	docsHref: string;
	tasks: string[];
	complete: WorkflowPredicate;
	/** Optional steps stay out of the progress denominator until they're done. */
	optional?: boolean;
	/** Hidden until the named entry in `WORKFLOW_FEATURES` flips to true. */
	requires?: WorkflowFeatureKey;
	enrichment?: WorkflowEnrichmentKey;
	signal?: (ctx: WorkflowStepSignalContext) => WorkflowStepSignal | null;
	/** What this step debits, if anything. See `spend.ts`. */
	spend?: WorkflowSpendDescriptor;
}

export type WorkflowScope = "organization" | "property";

export type WorkflowConcurrency = "shared" | "perTarget";

/**
 * How a playbook earns its progress.
 *
 * - `observed` — the standing-setup playbooks. "Track visibility over time" is
 *   a description of a property that is already configured, so a property that
 *   happens to be configured is genuinely part-way through it, run or no run.
 * - `explicit` — the situational playbooks. "Track a rebrand" is about a thing
 *   that starts on a date. Reading pre-existing state as progress told a client
 *   they were already tracking a rebrand they had never set up, so before a run
 *   exists these show no progress at all, and afterwards their steps anchor on
 *   work done since the run started wherever the observed state carries a
 *   timestamp to anchor on.
 */
export type WorkflowProgressMode = "observed" | "explicit";

export type WorkflowTargetKind =
	| "property"
	| "tag"
	| "period"
	| "location"
	| "coverageGroup"
	| "pipelineRun";

export interface WorkflowTargetSpec {
	kind: WorkflowTargetKind;
	/** Shown in the start dialog and in run lists ("Incident tag", "Page"). */
	label: string;
	defaultTag?: string;
	pipelineMode?: "draft" | "page" | "tracked";
}

export interface WorkflowDefinition {
	id: string;
	/** Stable playbook number from `docs/workflow-playbooks.md` (1..28). */
	wNumber: number;
	title: string;
	outcome: string;
	audiences: WorkflowAudienceKey[];
	/** Dashboard names for the library card's "uses" line. */
	uses: string[];
	scope: WorkflowScope;
	/** Org-scoped workflows that create and then bind a property. */
	bindsProperty?: boolean;
	bindSubjectType?: SubjectType;
	concurrency: WorkflowConcurrency;
	/** Whether progress is observed from standing state or starts at a run. */
	progress: WorkflowProgressMode;
	/** Required for `concurrency: "perTarget"`. */
	target?: WorkflowTargetSpec;
	isApplicable: (
		mode: SubjectModeConfig,
		org: WorkflowOrganizationState,
	) => boolean;
	/** From the queue in `docs/workflow-content-plan.md`. */
	articleSlug: string;
	articleStatus: "published" | "todo";
	caveats?: string[];
	/** `draft` keeps the playbook out of the library until its catalog PR. */
	status: "live" | "draft";
	steps: WorkflowStep[];
}

// ── Runs ───────────────────────────────────────────────────────────────────────

export interface WorkflowStepState {
	skippedAt?: string;
	markedDoneAt?: string;
	markedDoneByUserId?: string;
}

export interface WorkflowRunContext {
	tag?: string;
	period?: string;
	locationPropertyId?: string;
	coverageGroupId?: string;
	pipelineRunId?: string;
	/** Derived on bind; the uniqueness key for a per-target run. */
	targetKey?: string;
	/** Derived on bind; shown in run lists. */
	targetLabel?: string;
}

/**
 * The shape the evaluator needs from a persisted run. Deliberately structural:
 * PR 2 introduces the Prisma model, and this stays the contract the pure
 * evaluator is written against.
 */
export interface WorkflowRunLike {
	id: string;
	propertyId: string | null;
	/** ISO-8601. Compared lexicographically against event timestamps. */
	startedAt: string;
	startedByUserId: string | null;
	stepStates: Record<string, WorkflowStepState>;
	context: WorkflowRunContext;
}

// ── Evaluation results ─────────────────────────────────────────────────────────

export type WorkflowStepStatus = "done" | "skipped" | "current" | "upcoming";

export interface EvaluatedWorkflowStep {
	id: string;
	status: WorkflowStepStatus;
	/** The predicate over live state, ignoring any run overrides. */
	observed: boolean;
	markedDone: boolean;
	skipped: boolean;
	/** True when `requires` names a feature that is still off. */
	hidden: boolean;
	signal: WorkflowStepSignal | null;
	/** Resolved absolute href, or null when the base path is unknown. */
	href: string | null;
}

export type WorkflowLibraryStatus =
	| { kind: "not-started" }
	| { kind: "in-progress"; completed: number; total: number }
	| { kind: "complete" };

export interface EvaluatedWorkflow {
	workflowId: string;
	steps: EvaluatedWorkflowStep[];
	/** First step that is neither done nor skipped; null when complete. */
	currentIndex: number | null;
	progress: { done: number; total: number; percent: number };
	isComplete: boolean;
	libraryStatus: WorkflowLibraryStatus;
}
